import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, X } from 'lucide-react';

interface ROICalculatorProps {
    isOpen: boolean;
    onClose: () => void;
}

const ROICalculator: React.FC<ROICalculatorProps> = ({ isOpen, onClose }) => {
    const [avgJobValue, setAvgJobValue] = useState<number>(12000);
    const [missedCalls, setMissedCalls] = useState<number>(5);
    const [closeRate, setCloseRate] = useState<number>(25);
    const [lostRevenue, setLostRevenue] = useState<number>(0);

    useEffect(() => {
        const weeklyLoss = missedCalls * (closeRate / 100) * avgJobValue;
        const monthlyLoss = weeklyLoss * 4;
        setLostRevenue(monthlyLoss);
    }, [avgJobValue, missedCalls, closeRate]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-end md:items-center justify-center sm:p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
            <div className="w-full max-w-lg bg-zinc-900 md:rounded-[2rem] rounded-t-[2rem] shadow-2xl overflow-hidden border border-zinc-800 flex flex-col max-h-[90vh]">
                
                <div className="p-6 border-b border-zinc-800 flex justify-between items-center sticky top-0 bg-zinc-900 z-10">
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        <TrendingUp size={20} className="text-orange-500"/> Revenue Leak
                    </h2>
                    <button 
                        onClick={onClose}
                        className="p-2 bg-zinc-800 rounded-full text-zinc-400 hover:text-white transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="p-8 overflow-y-auto custom-scrollbar space-y-8">
                    
                    {/* Input Group */}
                    <div className="space-y-6">
                        <div>
                            <div className="flex justify-between mb-2">
                                <label className="text-sm font-medium text-zinc-400">Avg Job Value</label>
                                <span className="text-sm font-bold text-white">${avgJobValue.toLocaleString()}</span>
                            </div>
                            <input 
                                type="range" 
                                min="1000" 
                                max="30000" 
                                step="500"
                                value={avgJobValue}
                                onChange={(e) => setAvgJobValue(Number(e.target.value))}
                                className="w-full h-1.5 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-orange-500"
                            />
                        </div>

                        <div>
                            <div className="flex justify-between mb-2">
                                <label className="text-sm font-medium text-zinc-400">Missed Calls / Week</label>
                                <span className="text-sm font-bold text-white">{missedCalls} calls</span>
                            </div>
                            <input 
                                type="range" 
                                min="1" 
                                max="50" 
                                value={missedCalls}
                                onChange={(e) => setMissedCalls(Number(e.target.value))}
                                className="w-full h-1.5 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-orange-500"
                            />
                        </div>

                        <div>
                            <div className="flex justify-between mb-2">
                                <label className="text-sm font-medium text-zinc-400">Close Rate</label>
                                <span className="text-sm font-bold text-white">{closeRate}%</span>
                            </div>
                            <input 
                                type="range" 
                                min="5" 
                                max="80" 
                                value={closeRate}
                                onChange={(e) => setCloseRate(Number(e.target.value))}
                                className="w-full h-1.5 bg-zinc-800 rounded-lg appearance-none cursor-pointer accent-orange-500"
                            />
                        </div>
                    </div>

                    {/* Result */}
                    <div className="p-6 bg-red-900/10 border border-red-500/20 rounded-2xl text-center">
                        <span className="block text-red-400 font-medium text-xs uppercase tracking-widest mb-2">
                            Projected Monthly Loss
                        </span>
                        <div className="text-5xl font-bold text-red-500 flex items-center justify-center gap-1 mb-2">
                            <span className="text-3xl text-red-500/70">$</span>
                            {lostRevenue.toLocaleString()}
                        </div>
                        <p className="text-sm text-zinc-500">
                            Annual Impact: <span className="text-white font-semibold">${(lostRevenue * 12).toLocaleString()}</span>
                        </p>
                    </div>

                    <button 
                        onClick={onClose}
                        className="w-full bg-white hover:bg-zinc-200 text-black font-bold py-4 rounded-xl transition-all"
                    >
                        Fix This Leak
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ROICalculator;